import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserBadges } from '../components/UserBadges';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import {
  Heart,
  MessageCircle,
  Send,
  Plus,
  Clock,
  Image as ImageIcon,
  Video,
  FileText,
  Smile,
  X,
  Play,
  GraduationCap,
  Shield,
  Briefcase,
  MessageSquare,
  Bell,
  Award,
  BellOff,
  Check,
  MoreVertical,
  Users,
  ArrowLeft,
  Trash2,
  Megaphone,
  Pin,
  PinOff,
  Pencil,
  Link2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for threads
const threadIcons = {
  'graduation-cap': GraduationCap,
  'shield': Shield,
  'briefcase': Briefcase,
  'message-square': MessageSquare,
  'message-circle': MessageCircle,
  'users': Users,
  'megaphone': Megaphone,
};

// Renders [text](url) markdown links and bare URLs as safe clickable links
const RICH_TEXT_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"')\]]+)/g;
const renderRichText = (text) => {
  if (!text) return text;
  const parts = [];
  let last = 0;
  let key = 0;
  const re = new RegExp(RICH_TEXT_REGEX);
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const href = m[2] || m[3];
    const label = m[1] || m[3];
    parts.push(
      <a
        key={key++}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#34657f] font-medium underline underline-offset-2 hover:text-[#b34e00] break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {label}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const ForumPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [recognition, setRecognition] = useState(null);

  useEffect(() => {
    axios.get(`${API}/forum/recognition`).then((r) => setRecognition(r.data)).catch(() => {});
  }, []);
  const [searchParams] = useSearchParams();

  // Deep-link support: /forum?thread=<id> (used by global search)
  useEffect(() => {
    const t = searchParams.get('thread');
    if (t) setSelectedThread(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [threadPreferences, setThreadPreferences] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', thread_id: '' });
  const [commentInputs, setCommentInputs] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  
  // Thread management
  const [createThreadOpen, setCreateThreadOpen] = useState(false);
  const [newThread, setNewThread] = useState({ name: '', description: '', color: '#34657f', icon: 'message-square' });
  
  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  
  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCommentEmojiPicker, setShowCommentEmojiPicker] = useState({});

  // Edit post/comment state
  const [editingPost, setEditingPost] = useState(null); // {id, title, content}
  const [editingComment, setEditingComment] = useState(null); // {postId, commentId, content}
  const [savingEdit, setSavingEdit] = useState(false);

  // Add-a-link dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ url: '', text: '' });
  
  // GIF URL dialog
  const [gifDialogOpen, setGifDialogOpen] = useState(false);
  const [gifUrl, setGifUrl] = useState('');

  useEffect(() => {
    fetchThreads();
    fetchThreadPreferences();
    fetchUnreadCounts();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [selectedThread]);

  // Opening a thread marks its posts as read and clears the "New" badge
  useEffect(() => {
    if (!selectedThread || !user?.id) return;
    axios.post(`${API}/threads/${selectedThread}/mark-read`).catch(() => {});
    setUnreadCounts(prev => ({ ...prev, [selectedThread]: 0 }));
  }, [selectedThread, user?.id]);

  const fetchUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API}/threads/unread-counts`);
      setUnreadCounts(response.data);
    } catch (error) { /* badges are non-critical */ }
  };

  const fetchThreads = async () => {
    try {
      const response = await axios.get(`${API}/threads`);
      setThreads(response.data);
    } catch (error) {
      console.error('Error fetching threads:', error);
    }
  };

  const fetchThreadPreferences = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/threads/preferences/${user.id}`);
      const prefs = {};
      response.data.forEach(p => {
        prefs[p.thread_id] = { is_following: p.is_following, is_muted: p.is_muted };
      });
      setThreadPreferences(prefs);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const fetchPosts = async () => {
    setIsLoading(true);
    try {
      const params = selectedThread ? { thread_id: selectedThread } : {};
      const response = await axios.get(`${API}/posts`, { params });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load forum posts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateThread = async () => {
    if (!newThread.name.trim()) {
      toast.error('Please enter a thread name');
      return;
    }

    try {
      await axios.post(`${API}/threads`, newThread, {
        params: {
          created_by: user?.id || 'admin',
          user_role: user?.role || 'instructor'
        }
      });
      toast.success('Thread created!');
      setCreateThreadOpen(false);
      setNewThread({ name: '', description: '', color: '#34657f', icon: 'message-square' });
      fetchThreads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create thread');
    }
  };

  const handleToggleFollow = async (threadId) => {
    try {
      const response = await axios.post(`${API}/threads/${threadId}/follow`, null, {
        params: { user_id: user?.id || 'demo' }
      });
      setThreadPreferences(prev => ({
        ...prev,
        [threadId]: { ...prev[threadId], is_following: response.data.is_following }
      }));
      toast.success(response.data.is_following ? 'Following thread' : 'Unfollowed thread');
    } catch (error) {
      toast.error('Failed to update preference');
    }
  };

  const handleToggleMute = async (threadId) => {
    try {
      const response = await axios.post(`${API}/threads/${threadId}/mute`, null, {
        params: { user_id: user?.id || 'demo' }
      });
      setThreadPreferences(prev => ({
        ...prev,
        [threadId]: { ...prev[threadId], is_muted: response.data.is_muted }
      }));
      toast.success(response.data.is_muted ? 'Thread muted' : 'Thread unmuted');
    } catch (error) {
      toast.error('Failed to update preference');
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    const threadChoice = newPost.thread_id || selectedThread;
    if (!threadChoice) {
      toast.error('Please choose a topic for your post');
      return;
    }

    try {
      const response = await axios.post(`${API}/posts`, {
        title: newPost.title,
        content: newPost.content,
        thread_id: threadChoice,
        attachments: attachments
      }, {
        params: {
          author_id: user?.id || 'demo',
          author_name: user?.name || 'Demo User',
          user_role: user?.role || 'instructor'
        }
      });
      setPosts([response.data, ...posts]);
      setNewPost({ title: '', content: '', thread_id: '' });
      setAttachments([]);
      setNewPostOpen(false);
      toast.success('Post created successfully!');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/like`, null, {
        params: { user_id: user?.id || 'demo' }
      });
      
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, likes: response.data.liked 
              ? [...(post.likes || []), user?.id || 'demo']
              : (post.likes || []).filter(id => id !== (user?.id || 'demo'))
            }
          : post
      ));
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleComment = async (postId) => {
    const commentContent = commentInputs[postId];
    if (!commentContent?.trim()) return;

    try {
      const response = await axios.post(
        `${API}/posts/${postId}/comments`,
        { content: commentContent }
      );
      
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, comments: [...(post.comments || []), response.data] }
          : post
      ));
      setCommentInputs({ ...commentInputs, [postId]: '' });
      toast.success('Comment added!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handlePinPost = async (postId) => {
    try {
      const res = await axios.post(`${API}/posts/${postId}/pin`, null, {
        params: { user_role: user?.role || 'instructor' }
      });
      const pinned = res.data.is_pinned;
      setPosts(posts.map(p => p.id === postId
        ? { ...p, is_pinned: pinned, pinned_at: pinned ? new Date().toISOString() : null }
        : p));
      toast.success(pinned ? 'Post pinned to the top' : 'Post unpinned');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pin');
    }
  };

  const handleDeletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title || 'this post'}" and all its comments? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/posts/${post.id}`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success('Post deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  // File upload handlers
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingAttachment(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        formData.append('description', 'Forum attachment');
        formData.append('content_type', 'other');
        formData.append('created_by', user?.id || 'anonymous');
        formData.append('created_by_name', user?.name || 'Anonymous');
        
        const response = await axios.post(`${API}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const fileType = getFileType(file.name);
        
        setAttachments(prev => [...prev, {
          id: response.data.id,
          name: file.name,
          url: `${process.env.REACT_APP_BACKEND_URL}${response.data.file_url}`,
          type: fileType,
          size: file.size
        }]);
        
        toast.success(`Uploaded ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploadingAttachment(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileType = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
    if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext)) return 'video';
    return 'document';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddGif = () => {
    if (!gifUrl.trim()) {
      toast.error('Please enter a GIF URL');
      return;
    }
    
    if (!gifUrl.match(/\.(gif|gifv)($|\?)/i) && !gifUrl.includes('giphy.com') && !gifUrl.includes('tenor.com')) {
      toast.error('Please enter a valid GIF URL');
      return;
    }
    
    let embedUrl = gifUrl;
    if (gifUrl.includes('giphy.com') && !gifUrl.includes('media')) {
      const giphyId = gifUrl.split('-').pop().split('?')[0];
      embedUrl = `https://media.giphy.com/media/${giphyId}/giphy.gif`;
    }
    
    setAttachments(prev => [...prev, {
      id: `gif-${Date.now()}`,
      name: 'GIF',
      url: embedUrl,
      type: 'gif'
    }]);
    
    setGifUrl('');
    setGifDialogOpen(false);
    toast.success('GIF added!');
  };

  const handleEmojiSelect = (emoji) => {
    setNewPost(prev => ({ ...prev, content: prev.content + emoji.native }));
    setShowEmojiPicker(false);
  };

  const handleCommentEmojiSelect = (postId, emoji) => {
    setCommentInputs(prev => ({ ...prev, [postId]: (prev[postId] || '') + emoji.native }));
    setShowCommentEmojiPicker(prev => ({ ...prev, [postId]: false }));
  };

  const getInitials = (name) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getThreadById = (threadId) => threads.find(t => t.id === threadId);

  const renderAttachment = (attachment, index) => {
    if (attachment.type === 'image' || attachment.type === 'gif') {
      return (
        <div key={index} className="relative rounded-xl overflow-hidden bg-slate-100">
          <img src={attachment.url} alt={attachment.name} className="w-full max-h-96 object-contain" />
        </div>
      );
    }
    if (attachment.type === 'video') {
      return (
        <div key={index} className="relative rounded-xl overflow-hidden bg-black">
          <video src={attachment.url} controls className="w-full max-h-96" />
        </div>
      );
    }
    return (
      <a key={index} href={attachment.url} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors">
        <div className="w-10 h-10 rounded-lg bg-[#34657f] flex items-center justify-center">
          <FileText className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-[#1A1A1A] truncate">{attachment.name}</p>
          {attachment.size && <p className="text-sm text-[#5C5C5C]">{formatFileSize(attachment.size)}</p>}
        </div>
      </a>
    );
  };

  const renderAttachmentPreview = (attachment, index) => (
    <div key={index} className="relative group">
      {(attachment.type === 'image' || attachment.type === 'gif') ? (
        <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-100">
          <img src={attachment.url} alt={attachment.name} className="w-full h-full object-cover" />
        </div>
      ) : attachment.type === 'video' ? (
        <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-900 flex items-center justify-center">
          <Play className="w-8 h-8 text-white" />
        </div>
      ) : (
        <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-[#34657f] flex items-center justify-center">
          <FileText className="w-8 h-8 text-white" />
        </div>
      )}
      <button onClick={() => removeAttachment(index)}
        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <X className="w-4 h-4" />
      </button>
      <p className="text-xs text-[#5C5C5C] mt-1 truncate w-20">{attachment.name}</p>
    </div>
  );

  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';
  const isDemo = user?.role === 'demo';

  const announcementThread = threads.find(t => t.is_announcement);
  const announcementThreadId = announcementThread?.id;
  const viewingAnnouncements = selectedThread && selectedThread === announcementThreadId;
  // Non-admins may not create a post while inside the Announcements thread.
  const canCreatePost = !isDemo && !(viewingAnnouncements && !isAdmin);
  // Pinned posts float to the very top, then announcements, then the rest (by feed order).
  const sortedPosts = [...posts].sort((a, b) => {
    const aPin = a.is_pinned ? 1 : 0;
    const bPin = b.is_pinned ? 1 : 0;
    if (aPin !== bPin) return bPin - aPin;
    if (aPin && bPin) return (b.pinned_at || '').localeCompare(a.pinned_at || '');
    const aAnn = a.thread_id === announcementThreadId ? 1 : 0;
    const bAnn = b.thread_id === announcementThreadId ? 1 : 0;
    return bAnn - aAnn;
  });

  const saveEditPost = async () => {
    const title = editingPost.title.trim();
    const content = editingPost.content.trim();
    if (!title || !content) {
      toast.error('Title and content cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/posts/${editingPost.id}`, { title, content });
      setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, title, content, edited_at: new Date().toISOString() } : p));
      setEditingPost(null);
      toast.success('Post updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update post');
    } finally {
      setSavingEdit(false);
    }
  };

  const saveEditComment = async () => {
    const content = editingComment.content.trim();
    if (!content) {
      toast.error('Comment cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/posts/${editingComment.postId}/comments/${editingComment.commentId}`, { content });
      setPosts(prev => prev.map(p => p.id !== editingComment.postId ? p : {
        ...p,
        comments: p.comments.map(c => c.id === editingComment.commentId ? { ...c, content, edited_at: new Date().toISOString() } : c),
      }));
      setEditingComment(null);
      toast.success('Comment updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update comment');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleInsertLink = () => {
    let url = linkForm.url.trim();
    if (!url) {
      toast.error('Enter a URL');
      return;
    }
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    const text = linkForm.text.trim();
    const snippet = text ? `[${text}](${url})` : url;
    setNewPost(prev => ({ ...prev, content: prev.content ? `${prev.content} ${snippet}` : snippet }));
    setLinkDialogOpen(false);
    toast.success('Link added to your post');
  };

  const ThreadCard = ({ thread }) => {
    const Icon = threadIcons[thread.icon] || MessageSquare;
    const pref = threadPreferences[thread.id] || {};
    const postCount = posts.filter(p => p.thread_id === thread.id).length;
    const FollowIcon = pref.is_following ? Check : Bell;
    const unread = unreadCounts[thread.id] || 0;

    return (
      <Card 
        className={`dashboard-card relative cursor-pointer hover:border-[${thread.color}]/50 transition-all ${
          selectedThread === thread.id ? 'ring-2' : ''
        }`}
        style={{ 
          borderColor: selectedThread === thread.id ? thread.color : undefined,
          '--ring-color': thread.color 
        }}
        onClick={() => setSelectedThread(thread.id)}
        data-testid={`thread-card-${thread.id}`}
      >
        {unread > 0 && (
          <span
            className="absolute -top-2.5 -right-2 z-10 inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#b34e00] text-white text-[11px] font-bold shadow-md"
            data-testid={`unread-badge-${thread.id}`}
            aria-label={`${unread} new ${unread === 1 ? 'post' : 'posts'} in ${thread.name}`}
          >
            {unread} New
          </span>
        )}
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: thread.color }}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[#1A1A1A] [overflow-wrap:normal] [word-break:normal] hyphens-none">{thread.name}</h3>
              <p className="text-sm text-[#5C5C5C] line-clamp-2 mt-1">{thread.description}</p>
              <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                <span className="text-xs text-[#5C5C5C] shrink-0">{postCount} posts</span>
                {!isDemo && (
                  <div className="flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => handleToggleFollow(thread.id)}
                      className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold border transition-all duration-200 active:scale-95 hover:shadow-sm ${
                        pref.is_following ? 'text-white shadow-sm' : 'bg-white'
                      }`}
                      style={pref.is_following
                        ? { backgroundColor: thread.color, borderColor: thread.color }
                        : { borderColor: `${thread.color}66`, color: thread.color }}
                      title={pref.is_following ? 'You’ll see new posts from this thread — click to unfollow' : 'Follow to keep up with new posts in this thread'}
                      data-testid={`follow-thread-btn-${thread.id}`}
                    >
                      <FollowIcon className="w-3.5 h-3.5" />
                      {pref.is_following ? 'Following' : 'Follow'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleMute(thread.id)}
                      className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold border transition-all duration-200 active:scale-95 hover:shadow-sm ${
                        pref.is_muted
                          ? 'bg-slate-700 border-slate-700 text-white shadow-sm'
                          : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                      }`}
                      title={pref.is_muted ? 'This thread is muted — click to unmute' : 'Mute to stop hearing about this thread'}
                      data-testid={`mute-thread-btn-${thread.id}`}
                    >
                      <BellOff className="w-3.5 h-3.5" />
                      {pref.is_muted ? 'Muted' : 'Mute'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading && threads.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="forum-loading">
        <div className="w-8 h-8 border-4 border-[#34657f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="forum-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
        <div className="flex items-start gap-4">
          {selectedThread && (
            <Button variant="ghost" size="icon" aria-label="Back to threads" onClick={() => setSelectedThread(null)} data-testid="back-to-threads">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">
              {selectedThread ? getThreadById(selectedThread)?.name || 'Thread' : 'Community Forum'}
            </h1>
            <p className="text-[#5C5C5C] mt-2">
              {selectedThread 
                ? getThreadById(selectedThread)?.description 
                : 'Join discussions, share updates, and connect with the community'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap sm:justify-end shrink-0">
          {isAdmin && !selectedThread && (
            <Button variant="outline" onClick={() => setCreateThreadOpen(true)} data-testid="create-thread-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Thread
            </Button>
          )}
          {canCreatePost && (
            <Button className="bg-[#b34e00] hover:bg-[#a84d09]" onClick={() => {
              setNewPost({ ...newPost, thread_id: selectedThread || '' });
              setNewPostOpen(true);
            }} data-testid="create-post-btn">
              <Plus className="w-4 h-4 mr-2" />
              {viewingAnnouncements && isAdmin ? 'Post Announcement' : 'Create Post'}
            </Button>
          )}
        </div>
      </div>

      {/* Thread Cards - Show when no thread selected */}
      {!selectedThread && (
        <div className="space-y-4">
          {/* Pinned Announcements & Updates banner */}
          {announcementThread && (
            <Card
              className="relative cursor-pointer border-2 border-[#e87722]/40 bg-gradient-to-r from-[#e87722]/10 to-transparent hover:border-[#e87722] transition-all"
              onClick={() => setSelectedThread(announcementThread.id)}
              data-testid="announcements-banner"
            >
              {(unreadCounts[announcementThread.id] || 0) > 0 && (
                <span
                  className="absolute -top-2.5 -right-2 z-10 inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#b34e00] text-white text-[11px] font-bold shadow-md"
                  data-testid={`unread-badge-${announcementThread.id}`}
                  aria-label={`${unreadCounts[announcementThread.id]} new posts in ${announcementThread.name}`}
                >
                  {unreadCounts[announcementThread.id]} New
                </span>
              )}
              <CardContent className="p-6 flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl bg-[#b34e00] flex items-center justify-center shrink-0">
                  <Megaphone className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-[#1A1A1A]">{announcementThread.name}</h3>
                    <Badge className="bg-[#b34e00] text-white text-xs">Pinned</Badge>
                  </div>
                  <p className="text-sm text-[#5C5C5C] mt-1 line-clamp-2">{announcementThread.description}</p>
                </div>
                <span className="text-sm text-[#5C5C5C] shrink-0 hidden sm:block">
                  {posts.filter(p => p.thread_id === announcementThread.id).length} posts
                </span>
              </CardContent>
            </Card>
          )}

          {/* All Posts Button - Prominent on the left */}
          <div className="flex flex-wrap items-start gap-4">
            <Card 
              className={`dashboard-card cursor-pointer hover:border-[#34657f]/50 transition-all w-full sm:w-auto sm:min-w-[280px] ${
                !selectedThread ? 'ring-2 ring-[#34657f] border-[#34657f]' : ''
              }`}
              onClick={() => setSelectedThread(null)}
              data-testid="all-posts-btn"
            >
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#34657f] flex items-center justify-center shrink-0">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1A1A1A]">All Posts</h3>
                  <p className="text-sm text-[#5C5C5C]">{posts.length} total discussions</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Thread Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {threads.filter(t => !t.is_announcement).map(thread => (
              <ThreadCard key={thread.id} thread={thread} />
            ))}
          </div>

          {/* Community Recognition */}
          {recognition?.top_contributors?.length > 0 && (
            <Card className="dashboard-card" data-testid="community-recognition-card">
              <CardContent className="p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#b34e00] flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-['Manrope'] font-bold text-[#1A1A1A]">Community Recognition</p>
                </div>
                {recognition.top_contributors.map((u) => (
                  <div key={u.author_id} className="flex items-center gap-2" data-testid={`recognition-${u.author_id}`}>
                    <Avatar className="w-7 h-7">
                      <AvatarImage src={u.author_avatar} alt={u.author_name} />
                      <AvatarFallback className="text-[10px] bg-[#34657f] text-white">{getInitials(u.author_name)}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-[#1A1A1A]">{u.author_name}</span>
                    <UserBadges badgeIds={u.badges} defs={recognition.badge_definitions} max={4} size={16} />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Posts Feed */}
      <div className="max-w-3xl mx-auto space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#34657f] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length > 0 ? (
          sortedPosts.map((post) => {
            const postThread = getThreadById(post.thread_id);
            return (
              <Card key={post.id} className="dashboard-card overflow-hidden" data-testid={`forum-post-${post.id}`}>
                <CardContent className="p-0">
                  {/* Pinned Ribbon */}
                  {post.is_pinned && (
                    <div
                      className="px-6 py-2 text-sm font-semibold flex items-center gap-2 bg-amber-100 text-amber-800"
                      data-testid={`pinned-ribbon-${post.id}`}
                    >
                      <Pin className="w-4 h-4" />
                      Pinned
                    </div>
                  )}
                  {/* Thread Badge */}
                  {postThread && !selectedThread && (
                    <div 
                      className="px-6 py-2 text-sm font-medium flex items-center gap-2 cursor-pointer hover:opacity-80"
                      style={{ backgroundColor: `${postThread.color}15`, color: postThread.color }}
                      onClick={() => setSelectedThread(postThread.id)}
                    >
                      {React.createElement(threadIcons[postThread.icon] || MessageSquare, { className: "w-4 h-4" })}
                      {postThread.name}
                    </div>
                  )}
                  
                  {/* Post Header */}
                  <div className="p-6 pb-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={post.author_avatar} alt={post.author_name} />
                        <AvatarFallback className="bg-[#34657f] text-white font-semibold">
                          {getInitials(post.author_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#1A1A1A]">{post.author_name}</span>
                          {recognition?.user_badges?.[post.author_id] && (
                            <UserBadges badgeIds={recognition.user_badges[post.author_id]} defs={recognition.badge_definitions} max={3} size={15} />
                          )}
                          <span className="text-[#5C5C5C] text-sm flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(post.created_at)}
                          </span>
                          {post.edited_at && (
                            <span className="text-xs text-[#5C5C5C] italic" data-testid={`post-edited-${post.id}`}>(edited)</span>
                          )}
                        </div>
                        <h3 className="text-xl font-bold font-['Manrope'] text-[#1A1A1A] mt-2">{post.title}</h3>
                      </div>
                      {!isDemo && (isAdmin || post.author_id === user?.id) && (
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Post options" className="shrink-0 h-8 w-8" data-testid={`post-menu-${post.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => setEditingPost({ id: post.id, title: post.title, content: post.content })}
                              data-testid={`edit-post-${post.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit post
                            </DropdownMenuItem>
                            {isAdmin && (
                              <DropdownMenuItem onClick={() => handlePinPost(post.id)} data-testid={`pin-post-${post.id}`}>
                                {post.is_pinned
                                  ? <><PinOff className="w-4 h-4 mr-2" />Unpin post</>
                                  : <><Pin className="w-4 h-4 mr-2" />Pin to top</>}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDeletePost(post)}
                              className="text-red-700 focus:text-red-700"
                              data-testid={`delete-post-${post.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete post
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  
                  {editingPost?.id === post.id ? (
                    <div className="mt-4 space-y-3" data-testid={`edit-post-form-${post.id}`}>
                      <div className="space-y-1">
                        <Label htmlFor={`edit-title-${post.id}`} className="text-xs text-[#5C5C5C]">Title</Label>
                        <Input id={`edit-title-${post.id}`} value={editingPost.title}
                          onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                          data-testid={`edit-post-title-${post.id}`} />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`edit-content-${post.id}`} className="text-xs text-[#5C5C5C]">Content</Label>
                        <Textarea id={`edit-content-${post.id}`} rows={4} value={editingPost.content}
                          onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                          data-testid={`edit-post-content-${post.id}`} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditingPost(null)} data-testid={`edit-post-cancel-${post.id}`}>Cancel</Button>
                        <Button size="sm" className="bg-[#34657f] hover:bg-[#2a5060]" disabled={savingEdit}
                          onClick={saveEditPost} data-testid={`edit-post-save-${post.id}`}>
                          {savingEdit ? 'Saving…' : 'Save changes'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[#1A1A1A] mt-4 leading-relaxed whitespace-pre-wrap">{renderRichText(post.content)}</p>
                  )}
                    
                    {post.attachments && post.attachments.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {post.attachments.map((attachment, index) => (
                          typeof attachment === 'string' 
                            ? renderAttachment({ url: attachment, type: 'image', name: 'Image' }, index)
                            : renderAttachment(attachment, index)
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Post Actions */}
                  <div className="px-6 py-3 border-t border-slate-100 flex items-center gap-4">
                    <Button variant="ghost" size="sm"
                      className={`flex items-center gap-2 ${post.likes?.includes(user?.id || 'demo') ? 'text-red-700' : 'text-[#5C5C5C] hover:text-red-700'} ${isDemo ? 'pointer-events-none opacity-70' : ''}`}
                      onClick={() => !isDemo && handleLike(post.id)} data-testid={`like-btn-${post.id}`}>
                      <Heart className={`w-5 h-5 ${post.likes?.includes(user?.id || 'demo') ? 'fill-current' : ''}`} />
                      <span>{post.likes?.length || 0}</span>
                    </Button>
                    <Button variant="ghost" size="sm" className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#34657f]"
                      onClick={() => toggleComments(post.id)} data-testid={`comments-btn-${post.id}`}>
                      <MessageCircle className="w-5 h-5" />
                      <span>{post.comments?.length || 0} Comments</span>
                    </Button>
                  </div>

                  {/* Comments Section */}
                  {expandedComments[post.id] && (
                    <div className="px-6 pb-6 border-t border-slate-100 bg-[#F8F9FA]">
                      {post.comments?.length > 0 && (
                        <div className="space-y-4 pt-4 mb-4">
                          {post.comments.map((comment) => (
                            <div key={comment.id} className="flex gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={comment.author_avatar} alt={comment.author_name} />
                                <AvatarFallback className="bg-[#b34e00] text-white text-xs">
                                  {getInitials(comment.author_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 bg-white rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-sm text-[#1A1A1A]">{comment.author_name}</span>
                                  {recognition?.user_badges?.[comment.author_id] && (
                                    <UserBadges badgeIds={recognition.user_badges[comment.author_id]} defs={recognition.badge_definitions} max={2} size={13} />
                                  )}
                                  <span className="text-xs text-[#5C5C5C]">{formatDate(comment.created_at)}</span>
                                  {comment.edited_at && (
                                    <span className="text-xs text-[#5C5C5C] italic" data-testid={`comment-edited-${comment.id}`}>(edited)</span>
                                  )}
                                  {!isDemo && (isAdmin || comment.author_id === user?.id) && editingComment?.commentId !== comment.id && (
                                    <button
                                      type="button"
                                      onClick={() => setEditingComment({ postId: post.id, commentId: comment.id, content: comment.content })}
                                      className="ml-auto text-slate-400 hover:text-[#34657f] p-1 rounded"
                                      title="Edit comment"
                                      aria-label="Edit comment"
                                      data-testid={`edit-comment-${comment.id}`}
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                                {editingComment?.commentId === comment.id ? (
                                  <div className="space-y-2" data-testid={`edit-comment-form-${comment.id}`}>
                                    <Textarea rows={2} value={editingComment.content} className="text-sm"
                                      onChange={(e) => setEditingComment({ ...editingComment, content: e.target.value })}
                                      data-testid={`edit-comment-input-${comment.id}`} />
                                    <div className="flex justify-end gap-2">
                                      <Button variant="outline" size="sm" onClick={() => setEditingComment(null)} data-testid={`edit-comment-cancel-${comment.id}`}>Cancel</Button>
                                      <Button size="sm" className="bg-[#34657f] hover:bg-[#2a5060]" disabled={savingEdit}
                                        onClick={saveEditComment} data-testid={`edit-comment-save-${comment.id}`}>
                                        {savingEdit ? 'Saving…' : 'Save'}
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{renderRichText(comment.content)}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {isDemo ? (
                        <div className="pt-4 text-center text-sm text-[#5C5C5C] italic">
                          Demo accounts can read all posts but cannot comment, like, or post.
                        </div>
                      ) : (
                      <div className="flex gap-3 pt-4">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.avatar_url} alt={user?.name} />
                          <AvatarFallback className="bg-[#34657f] text-white text-xs">
                            {getInitials(user?.name || 'User')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 flex gap-2">
                          <div className="flex-1 relative">
                            <Input placeholder="Write a comment..." value={commentInputs[post.id] || ''}
                              onChange={(e) => setCommentInputs({ ...commentInputs, [post.id]: e.target.value })}
                              className="pr-10"
                              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(post.id); }}}
                              data-testid={`comment-input-${post.id}`} />
                            <Popover open={showCommentEmojiPicker[post.id]} 
                              onOpenChange={(open) => setShowCommentEmojiPicker(prev => ({ ...prev, [post.id]: open }))}>
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Add emoji" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7">
                                  <Smile className="w-4 h-4 text-slate-400" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="end">
                                <Picker data={data} onEmojiSelect={(emoji) => handleCommentEmojiSelect(post.id, emoji)}
                                  theme="light" previewPosition="none" skinTonePosition="none" />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <Button size="icon" aria-label="Send reply" className="bg-[#34657f] hover:bg-[#2a5060]"
                            onClick={() => handleComment(post.id)} data-testid={`send-comment-${post.id}`}>
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="dashboard-card">
            <CardContent className="py-12 text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
                {selectedThread ? 'No posts in this thread yet' : 'No posts yet'}
              </h3>
              <p className="text-[#5C5C5C] mb-4">
                {viewingAnnouncements && !isAdmin
                  ? 'Announcements from the Project LIFE team will appear here. You can reply and react once they post.'
                  : isDemo ? 'Posts will appear here once instructors start sharing.' : 'Be the first to share something!'}
              </p>
              {canCreatePost && (
                <Button onClick={() => {
                  setNewPost({ ...newPost, thread_id: selectedThread || '' });
                  setNewPostOpen(true);
                }} className="bg-[#b34e00] hover:bg-[#a84d09]">
                  <Plus className="w-4 h-4 mr-2" />
                  {viewingAnnouncements && isAdmin ? 'Post First Announcement' : 'Create First Post'}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Thread Dialog (Admin Only) */}
      <Dialog open={createThreadOpen} onOpenChange={setCreateThreadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Create New Thread</DialogTitle>
            <DialogDescription>Create a discussion thread for the community.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="thread-name">Thread Name *</Label>
              <Input id="thread-name" placeholder="e.g., Success Stories" value={newThread.name}
                onChange={(e) => setNewThread({ ...newThread, name: e.target.value })} data-testid="thread-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="thread-desc">Description</Label>
              <Textarea id="thread-desc" placeholder="What is this thread about?" value={newThread.description}
                onChange={(e) => setNewThread({ ...newThread, description: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Color</Label>
                <Select value={newThread.color} onValueChange={(v) => setNewThread({ ...newThread, color: v })}>
                  <SelectTrigger aria-label="Thread color">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: newThread.color }} />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="#34657f"><div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-[#34657f]" /> Blue</div></SelectItem>
                    <SelectItem value="#6cc24a"><div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-[#3d7a22]" /> Green</div></SelectItem>
                    <SelectItem value="#e87722"><div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-[#b34e00]" /> Orange</div></SelectItem>
                    <SelectItem value="#e87722"><div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-[#b34e00]" /> Yellow</div></SelectItem>
                    <SelectItem value="#7B1FA2"><div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-[#7B1FA2]" /> Purple</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select value={newThread.icon} onValueChange={(v) => setNewThread({ ...newThread, icon: v })}>
                  <SelectTrigger aria-label="Thread icon"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="message-square"><div className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Chat</div></SelectItem>
                    <SelectItem value="graduation-cap"><div className="flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Education</div></SelectItem>
                    <SelectItem value="shield"><div className="flex items-center gap-2"><Shield className="w-4 h-4" /> Admin</div></SelectItem>
                    <SelectItem value="briefcase"><div className="flex items-center gap-2"><Briefcase className="w-4 h-4" /> Jobs</div></SelectItem>
                    <SelectItem value="users"><div className="flex items-center gap-2"><Users className="w-4 h-4" /> Team</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateThreadOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateThread} className="bg-[#34657f]" data-testid="submit-thread-btn">Create Thread</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Post Dialog */}
      <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Create New Post</DialogTitle>
            <DialogDescription>Share updates, photos, videos, or documents with the community.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Thread Selection */}
            <div className="space-y-2">
              <Label>Topic *</Label>
              <Select value={newPost.thread_id || selectedThread || ''} onValueChange={(v) => setNewPost({ ...newPost, thread_id: v })}>
                <SelectTrigger aria-label="Select a topic" data-testid="post-thread-select">
                  <SelectValue placeholder="Choose a topic…" />
                </SelectTrigger>
                <SelectContent>
                  {threads
                    .filter(thread => isAdmin || !thread.is_announcement)
                    .map(thread => (
                    <SelectItem key={thread.id} value={thread.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: thread.color }} />
                        {thread.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="post-title">Title</Label>
              <Input id="post-title" placeholder="Enter post title..." value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })} className="h-12" data-testid="post-title-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="post-content">Content</Label>
              <div className="relative">
                <Textarea id="post-content" placeholder="Share your thoughts..." value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })} rows={5} className="pr-10" data-testid="post-content-input" />
                <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Add emoji" className="absolute right-2 top-2 h-8 w-8" data-testid="emoji-picker-btn">
                      <Smile className="w-5 h-5 text-slate-400 hover:text-[#b34e00]" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Picker data={data} onEmojiSelect={handleEmojiSelect} theme="light" previewPosition="none" skinTonePosition="none" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            {/* Attachment Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx"
                onChange={handleFileUpload} className="hidden" id="attachment-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAttachment} data-testid="attach-file-btn">
                <ImageIcon className="w-4 h-4 mr-2" />Photo
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                fileInputRef.current?.setAttribute('accept', 'video/*'); fileInputRef.current?.click();
              }} disabled={uploadingAttachment} data-testid="attach-video-btn">
                <Video className="w-4 h-4 mr-2" />Video
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                fileInputRef.current?.setAttribute('accept', '.pdf,.doc,.docx,.ppt,.pptx'); fileInputRef.current?.click();
              }} disabled={uploadingAttachment} data-testid="attach-doc-btn">
                <FileText className="w-4 h-4 mr-2" />Document
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setGifDialogOpen(true)} data-testid="attach-gif-btn">
                <span className="text-sm font-bold mr-1">GIF</span>
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { setLinkForm({ url: '', text: '' }); setLinkDialogOpen(true); }} data-testid="attach-link-btn">
                <Link2 className="w-4 h-4 mr-2" />Link
              </Button>
              {uploadingAttachment && <span className="text-sm text-[#5C5C5C]">Uploading...</span>}
            </div>
            
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-3 p-3 bg-[#F8F9FA] rounded-lg">
                {attachments.map((attachment, index) => renderAttachmentPreview(attachment, index))}
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => { setNewPostOpen(false); setAttachments([]); }} data-testid="cancel-post-btn">Cancel</Button>
              <Button onClick={handleCreatePost} className="bg-[#34657f] hover:bg-[#2a5060]" data-testid="submit-post-btn">Publish Post</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#34657f]" />
              Add a link
            </DialogTitle>
            <DialogDescription>The link will appear as clickable text in your post.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="link-url">URL *</Label>
              <Input id="link-url" placeholder="https://example.com" value={linkForm.url}
                onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInsertLink(); } }}
                data-testid="link-url-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="link-text">Display text (optional)</Label>
              <Input id="link-text" placeholder="e.g., Check out this resource" value={linkForm.text}
                onChange={(e) => setLinkForm({ ...linkForm, text: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInsertLink(); } }}
                data-testid="link-text-input" />
              <p className="text-xs text-[#5C5C5C]">If left blank, the URL itself is shown.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)} data-testid="link-cancel-btn">Cancel</Button>
            <Button onClick={handleInsertLink} className="bg-[#34657f] hover:bg-[#2a5060]" data-testid="link-insert-btn">Insert link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GIF URL Dialog */}
      <Dialog open={gifDialogOpen} onOpenChange={setGifDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl flex items-center gap-2">
              <span className="text-lg font-bold text-[#b34e00]">GIF</span>Add a GIF
            </DialogTitle>
            <DialogDescription>Paste a GIF URL from GIPHY, Tenor, or any direct .gif link</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="gif-url">GIF URL</Label>
              <Input id="gif-url" placeholder="https://giphy.com/..." value={gifUrl}
                onChange={(e) => setGifUrl(e.target.value)} data-testid="gif-url-input" />
              <p className="text-xs text-[#5C5C5C]">
                Find GIFs at <a href="https://giphy.com" target="_blank" rel="noopener noreferrer" className="text-[#34657f] hover:underline">GIPHY</a> or <a href="https://tenor.com" target="_blank" rel="noopener noreferrer" className="text-[#34657f] hover:underline">Tenor</a>
              </p>
            </div>
            {gifUrl && (gifUrl.includes('giphy') || gifUrl.includes('tenor') || gifUrl.match(/\.gif/i)) && (
              <div className="rounded-lg overflow-hidden bg-slate-100 max-h-48">
                <img src={gifUrl.includes('giphy.com') && !gifUrl.includes('media') 
                  ? `https://media.giphy.com/media/${gifUrl.split('-').pop().split('?')[0]}/giphy.gif` : gifUrl}
                  alt="GIF Preview" className="w-full h-full object-contain" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGifDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddGif} className="bg-[#b34e00] hover:bg-[#a84d09]" data-testid="add-gif-btn">Add GIF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForumPage;
